// Coupons — list + create + edit + archive. Replaces the Pricing
// list-only stub with the full CRUD surface.

import { useState } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { DataTable, type DataTableColumn } from '@drivecars/admin-ui';
import { PageHeader, ToolBtn } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import {
  archiveCoupon,
  createCoupon,
  fetchAdminCouponsList,
  updateCoupon,
  type AdminCouponDetail,
  type CouponWriteInput,
} from '@/lib/admin-api';
import { usePager } from '@/lib/use-pager';

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString() : '—';

interface ActionCtx {
  busyId: number | null;
  onEdit: (r: AdminCouponDetail) => void;
  onArchive: (r: AdminCouponDetail) => void;
}

const buildColumns = (ctx: ActionCtx): DataTableColumn<AdminCouponDetail>[] => [
  {
    key: 'code',
    h: 'Code',
    mono: true,
    cell: (r) => (
      <div className="flex items-center gap-2">
        <span className="font-semibold">{r.code}</span>
        {!r.enabled ? (
          <span className="rounded bg-[color:var(--bo-bg)] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
            Disabled
          </span>
        ) : null}
      </div>
    ),
  },
  {
    key: 'value',
    h: 'Discount',
    right: true,
    cell: (r) => (
      <span className="tabular-nums">
        {r.kind === 'percent' ? `${r.value}%` : `${r.currency ?? 'EUR'} ${r.value.toFixed(2)}`}
      </span>
    ),
  },
  {
    key: 'usage',
    h: 'Usage',
    right: true,
    cell: (r) => (
      <span className="tabular-nums text-[color:var(--bo-mute)]">
        {r.usageCount}
        {r.maxUses != null ? ` / ${r.maxUses}` : ''}
      </span>
    ),
  },
  {
    key: 'redemptions',
    h: 'Redemptions',
    right: true,
    cell: (r) => <span className="tabular-nums">{r.redemptions ?? 0}</span>,
  },
  { key: 'startAt', h: 'Starts', cell: (r) => fmtDate(r.startAt) },
  { key: 'endAt', h: 'Ends', cell: (r) => fmtDate(r.endAt) },
  {
    key: 'actions',
    h: 'Actions',
    cell: (r) => {
      const busy = ctx.busyId === r.id;
      return (
        <div className="flex flex-wrap items-center gap-1.5">
          <RowAction busy={busy} onClick={() => ctx.onEdit(r)}>
            Edit
          </RowAction>
          {r.enabled ? (
            <RowAction kind="danger" busy={busy} onClick={() => ctx.onArchive(r)}>
              Archive
            </RowAction>
          ) : null}
        </div>
      );
    },
  },
];

const EMPTY_DRAFT: CouponWriteInput = {
  code: '',
  kind: 'percent',
  value: 10,
  enabled: true,
};

export function Coupons() {
  const queryClient = useQueryClient();
  const { page, setPage, limit, setLimit } = usePager();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'coupons-list', page],
    queryFn: ({ signal }) => fetchAdminCouponsList({ page, limit }, signal),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'coupons-list'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'coupons'] });
  };
  const createMut = useMutation({ mutationFn: createCoupon, onSuccess: invalidate });
  const updateMut = useMutation({
    mutationFn: ({ id, data: d }: { id: number; data: Partial<CouponWriteInput> }) =>
      updateCoupon(id, d),
    onSuccess: invalidate,
  });
  const archiveMut = useMutation({ mutationFn: archiveCoupon, onSuccess: invalidate });

  const [editorState, setEditorState] = useState<{
    mode: 'create' | 'edit';
    id?: number;
    draft: CouponWriteInput;
  } | null>(null);

  const busyId = (() => {
    if (updateMut.isPending && updateMut.variables) return updateMut.variables.id;
    if (archiveMut.isPending && typeof archiveMut.variables === 'number')
      return archiveMut.variables;
    return null;
  })();

  const cols = buildColumns({
    busyId,
    onEdit: (r) =>
      setEditorState({
        mode: 'edit',
        id: r.id,
        draft: {
          code: r.code,
          kind: r.kind,
          value: r.value,
          currency: r.currency ?? undefined,
          enabled: r.enabled,
          startsAt: r.startAt,
          endsAt: r.endAt,
          usageLimit: r.maxUses,
          perUserLimit: r.perUserLimit,
          minTotal: r.minTotal,
        },
      }),
    onArchive: (r) => archiveMut.mutate(r.id),
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const mutationError = createMut.error || updateMut.error || archiveMut.error;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Commerce"
        title="Coupons"
        sub={`${total.toLocaleString()} coupons defined.`}
        actions={
          <ToolBtn
            icon="plus"
            primary
            onClick={() => setEditorState({ mode: 'create', draft: { ...EMPTY_DRAFT } })}
          >
            New coupon
          </ToolBtn>
        }
      />
      {error ? (
        <div role="alert" className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]">
          Couldn&apos;t load coupons: {(error as Error).message}
        </div>
      ) : null}
      {mutationError ? (
        <div role="alert" className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]">
          Mutation failed: {(mutationError as Error).message}
        </div>
      ) : null}
      <DataTable
        columns={cols}
        rows={rows}
        emptyLabel={isLoading ? 'Loading…' : 'No coupons yet.'}
        pagination={{ page, limit, total, onPageChange: setPage, onLimitChange: setLimit }}
      />

      {editorState ? (
        <CouponEditor
          mode={editorState.mode}
          draft={editorState.draft}
          busy={createMut.isPending || updateMut.isPending}
          onClose={() => setEditorState(null)}
          onSave={(payload) => {
            if (editorState.mode === 'create') {
              createMut.mutate(payload, { onSuccess: () => setEditorState(null) });
            } else if (editorState.id) {
              updateMut.mutate(
                { id: editorState.id, data: payload },
                { onSuccess: () => setEditorState(null) },
              );
            }
          }}
        />
      ) : null}
    </div>
  );
}

interface EditorProps {
  mode: 'create' | 'edit';
  draft: CouponWriteInput;
  busy: boolean;
  onClose: () => void;
  onSave: (payload: CouponWriteInput) => void;
}

function CouponEditor({ mode, draft: initial, busy, onClose, onSave }: EditorProps) {
  const [draft, setDraft] = useState<CouponWriteInput>(initial);
  const set = <K extends keyof CouponWriteInput>(k: K, v: CouponWriteInput[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 px-3"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-[16px] font-semibold tracking-tight">
            {mode === 'create' ? 'New coupon' : 'Edit coupon'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-[12px] text-[color:var(--bo-mute)] hover:text-[color:var(--bo-ink)]"
          >
            Close
          </button>
        </div>
        <div className="space-y-3">
          <label className="block">
            <span className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
              Code
            </span>
            <input
              type="text"
              required
              value={draft.code}
              onChange={(e) => set('code', e.target.value.toUpperCase())}
              disabled={mode === 'edit'}
              className="mt-1 h-10 w-full rounded-lg border border-[color:var(--bo-line)] px-3 font-mono text-[13.5px] outline-none focus:border-[color:var(--bo-accent)] disabled:bg-[color:var(--bo-bg)]"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
                Kind
              </span>
              <select
                value={draft.kind}
                onChange={(e) => set('kind', e.target.value as 'fixed' | 'percent')}
                className="mt-1 h-10 w-full rounded-lg border border-[color:var(--bo-line)] px-3 text-[13.5px] outline-none focus:border-[color:var(--bo-accent)]"
              >
                <option value="percent">Percent</option>
                <option value="fixed">Fixed</option>
              </select>
            </label>
            <label className="block">
              <span className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
                Value
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={draft.value}
                onChange={(e) => set('value', Number(e.target.value))}
                className="mt-1 h-10 w-full rounded-lg border border-[color:var(--bo-line)] px-3 text-[13.5px] outline-none focus:border-[color:var(--bo-accent)]"
              />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
                Starts (ISO date)
              </span>
              <input
                type="datetime-local"
                value={draft.startsAt ? draft.startsAt.slice(0, 16) : ''}
                onChange={(e) => set('startsAt', e.target.value ? new Date(e.target.value).toISOString() : null)}
                className="mt-1 h-10 w-full rounded-lg border border-[color:var(--bo-line)] px-3 text-[13.5px] outline-none focus:border-[color:var(--bo-accent)]"
              />
            </label>
            <label className="block">
              <span className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
                Ends
              </span>
              <input
                type="datetime-local"
                value={draft.endsAt ? draft.endsAt.slice(0, 16) : ''}
                onChange={(e) => set('endsAt', e.target.value ? new Date(e.target.value).toISOString() : null)}
                className="mt-1 h-10 w-full rounded-lg border border-[color:var(--bo-line)] px-3 text-[13.5px] outline-none focus:border-[color:var(--bo-accent)]"
              />
            </label>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
                Max uses
              </span>
              <input
                type="number"
                min="0"
                value={draft.usageLimit ?? ''}
                onChange={(e) =>
                  set('usageLimit', e.target.value === '' ? null : Number(e.target.value))
                }
                placeholder="∞"
                className="mt-1 h-10 w-full rounded-lg border border-[color:var(--bo-line)] px-3 text-[13.5px] outline-none focus:border-[color:var(--bo-accent)]"
              />
            </label>
            <label className="block">
              <span className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
                Per user
              </span>
              <input
                type="number"
                min="0"
                value={draft.perUserLimit ?? ''}
                onChange={(e) =>
                  set('perUserLimit', e.target.value === '' ? null : Number(e.target.value))
                }
                placeholder="∞"
                className="mt-1 h-10 w-full rounded-lg border border-[color:var(--bo-line)] px-3 text-[13.5px] outline-none focus:border-[color:var(--bo-accent)]"
              />
            </label>
            <label className="block">
              <span className="text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]">
                Min total
              </span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={draft.minTotal ?? ''}
                onChange={(e) =>
                  set('minTotal', e.target.value === '' ? null : Number(e.target.value))
                }
                className="mt-1 h-10 w-full rounded-lg border border-[color:var(--bo-line)] px-3 text-[13.5px] outline-none focus:border-[color:var(--bo-accent)]"
              />
            </label>
          </div>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={Boolean(draft.enabled)}
              onChange={(e) => set('enabled', e.target.checked)}
            />
            <span className="text-[13px]">Enabled</span>
          </label>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 items-center rounded-lg border border-[color:var(--bo-line)] px-4 text-[12.5px] font-medium text-[color:var(--bo-ink)] hover:border-[color:var(--bo-mute)]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave(draft)}
            disabled={busy || !draft.code.trim()}
            className="inline-flex h-10 items-center rounded-lg bg-[color:var(--bo-ink)] px-4 text-[12.5px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
